// One-time-payment tier model. Imported by server (gates), web client
// (upgrade modal + checkout flow), and native (upgrade modal + deep link
// to web). Pure values + helpers, no IO — keeps Metro and Vite happy.

export const PAYMENT_TIER_FREE = 'free' as const;
export const PAYMENT_TIER_SILVER = 'silver' as const;
export const PAYMENT_TIER_GOLD = 'gold' as const;
export const PAYMENT_TIER_PLATINUM = 'platinum' as const;

export type PaymentTier =
  | typeof PAYMENT_TIER_FREE
  | typeof PAYMENT_TIER_SILVER
  | typeof PAYMENT_TIER_GOLD
  | typeof PAYMENT_TIER_PLATINUM;

export const PAYMENT_TIERS = [
  PAYMENT_TIER_FREE,
  PAYMENT_TIER_SILVER,
  PAYMENT_TIER_GOLD,
  PAYMENT_TIER_PLATINUM,
] as const;

export const PAID_TIERS = [PAYMENT_TIER_SILVER, PAYMENT_TIER_GOLD, PAYMENT_TIER_PLATINUM] as const;
export type PaidTier = (typeof PAID_TIERS)[number];

export interface TierLimits {
  // null = unlimited.
  ownedGroups: number | null;
  membersPerGroup: number | null;
}

// Single source of truth. Server gates and client UX both read from here.
export const TIER_LIMITS: Record<PaymentTier, TierLimits> = {
  free: { ownedGroups: 0, membersPerGroup: 0 },
  // Silver: owner + 2 invitees = 3 total members in the owner's one group.
  silver: { ownedGroups: 1, membersPerGroup: 3 },
  gold: { ownedGroups: 1, membersPerGroup: null },
  platinum: { ownedGroups: null, membersPerGroup: null },
};

export const TIER_RANK: Record<PaymentTier, number> = {
  free: 0,
  silver: 1,
  gold: 2,
  platinum: 3,
};

export function isUpgrade(from: PaymentTier, to: PaymentTier): boolean {
  return TIER_RANK[to] > TIER_RANK[from];
}

export function canCreateGroup(tier: PaymentTier, ownedCount: number): boolean {
  const cap = TIER_LIMITS[tier].ownedGroups;
  return cap === null || ownedCount < cap;
}

export function canAddMember(tier: PaymentTier, currentMemberCount: number): boolean {
  const cap = TIER_LIMITS[tier].membersPerGroup;
  return cap === null || currentMemberCount < cap;
}

// Platinum can place predictions outside the 24h window — kickoff still gates
// them, and matches with placeholder teams are still blocked by
// TEAMS_UNRESOLVED.
export function canPredictEarly(tier: PaymentTier): boolean {
  return TIER_RANK[tier] >= TIER_RANK[PAYMENT_TIER_PLATINUM];
}

// Gold and platinum can view other group members' predictions before
// kickoff. Distinct from canPredictEarly: viewing-early is the lower tier
// perk; submitting-early stays platinum-only.
export function canViewPredictionsEarly(tier: PaymentTier): boolean {
  return TIER_RANK[tier] >= TIER_RANK[PAYMENT_TIER_GOLD];
}

// Smallest paid tier that would unblock the action — drives the
// "Recommended" highlight in the upgrade modal. Returns null only when even
// platinum couldn't unblock, which today is impossible by construction.
export function minTierToCreateGroup(ownedCount: number): PaidTier | null {
  for (const tier of PAID_TIERS) {
    if (canCreateGroup(tier, ownedCount)) return tier;
  }
  return null;
}

export function minTierForMemberCount(currentMemberCount: number): PaidTier | null {
  for (const tier of PAID_TIERS) {
    if (canAddMember(tier, currentMemberCount)) return tier;
  }
  return null;
}

// Server returns this on 402 responses; the client deserializes it from
// ApiError.payload to render the upgrade modal off the same response that
// blocked the action.
export interface QuotaPayload {
  currentTier: PaymentTier;
  requiredTier: PaidTier | null;
  currentCount: number;
  // null = unlimited (this branch shouldn't show up in a quota error,
  // but the type matches TierLimits to keep callers honest).
  limit: number | null;
}

// Server-provided tier catalog entry — name/description/features hardcoded
// server-side, price fetched live from Stripe.
//
// `iapProductIds` carries the Apple App Store / Google Play product
// identifiers the native client passes to RevenueCat. They are configured
// per-tier-per-platform via env vars on the server and surfaced here so the
// server stays the single source of truth for the catalog. `null` means the
// platform isn't wired yet — native must skip in-app purchase and fall back
// to the web deep-link path. Web ignores these fields entirely.
export interface TierCatalogEntry {
  tier: PaymentTier;
  name: string;
  description: string;
  features: string[];
  price: {
    unitAmount: number; // smallest currency unit (e.g. cents)
    currency: string; // ISO-4217 lowercase, per Stripe convention
    formatted: string; // pre-formatted for direct display
  } | null; // null for the free tier
  iapProductIds: {
    ios: string | null;
    android: string | null;
  };
}
